import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../supabase';
import { rpcAll } from '../lib/rpcAll';
import { fetchInventoryByCC as cortexInventoryByCC } from '../pages/theBarn/utils';
import { getEffectiveLaunchDate, getAllocationNeedDate, formatDateString, parseLocalDate, poToDateInputs } from '../utils/dateUtils';
import { syncGlobalConfig, loadCalcSettings } from '../utils/appConfig';

const DataContext = createContext(null);

// Helper to get month name from date
const getMonthName = (d) => {
  if (!d) return 'Unknown';
  const date = new Date(d);
  if (isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString('en-US', { month: 'short' });
};

// Helper to get launch season from date (Jan-Jun = Spring/Summer, Jul-Dec = Fall/Winter)
const getLaunchSeason = (d) => {
  if (!d) return 'Unknown';
  const date = new Date(d);
  if (isNaN(date.getTime())) return 'Unknown';
  const m = date.getMonth() + 1; // 1-12
  const y = date.getFullYear();
  return m <= 6 ? `${y} Spring/Summer` : `${y} Fall/Winter`;
};

// Compute Launch Season based on Launch Status
// - Launch PO: Use Launch Date
// - Carryover PO: Use Est In DC Date + lead time (requires Est In DC)
// - Blank/Empty: Use Est In DC Date + lead time
const computeLaunchSeason = (row, effectiveLaunchDate, isCarryover = false) => {
  // Use the pre-computed effective launch date if available
  if (effectiveLaunchDate) {
    return getLaunchSeason(effectiveLaunchDate);
  }
  // For Carryover POs without Est In DC, don't fall back to launch_date
  if (isCarryover) {
    return 'Missing Est In DC';
  }
  // Fallback to launch_date for non-Carryover POs
  return getLaunchSeason(row.launch_date);
};

// Field mapping: Supabase column names → App field names (for compatibility)
const mapPOFromSupabase = (row) => {
  // Canonical adapter — the ONE mapping from raw PO columns to date inputs
  const tempPO = poToDateInputs(row);

  // Use centralized function to calculate effective launch date
  const effectiveLaunchDateObj = getEffectiveLaunchDate(tempPO, loadCalcSettings());

  // For Carryover POs: don't fall back to launch_date (it's the original product launch, not relevant)
  // Instead, leave null so they show as "Missing Est In DC"
  const isCarryover = (row.launch_status || '').toLowerCase().includes('carryover');
  const effectiveLaunchDate = formatDateString(effectiveLaunchDateObj) || (isCarryover ? null : row.launch_date);

  // Flag for missing Est In DC on Carryover POs
  const missingEstInDC = isCarryover && !row.est_in_dc_date;

  // Pre-compute allocation need date using centralized function
  const allocNeedDateObj = getAllocationNeedDate(effectiveLaunchDateObj, tempPO.channel, loadCalcSettings());
  const allocNeedDate = formatDateString(allocNeedDateObj);

  return {
  // Keep original Supabase fields
  ...row,
  // Record type (PO or Receipt)
  record_type: row.record_type || 'PO',
  receiptNumber: row.receipt_number,
  receiptDate: row.receipt_date,
  // NetSuite internal IDs for linking
  po_internal_id: row.po_internal_id,
  // Map to legacy field names used throughout the app
  poNumber: row.document_number,
  shipmentNumber: row.shipment_number || '',
  productName: row.display_name?.split(' / ')[0] || row.product_style || row.display_name,
  colorName: row.color || '',
  category: row.division || '',
  vendor: row.vendor_name || '',
  poTerms: row.po_terms || '',
  launchDate: row.launch_date,  // Raw launch date from DB - for display in tables
  effectiveLaunchDate: effectiveLaunchDate,  // Calculated effective launch - for allocation logic
  allocNeedDate: allocNeedDate,  // Pre-computed allocation need date
  // Additional launch dates for Launch Deck
  itemRelaunchDate: row.item_relaunch_date || null,
  itemRetailRelaunchDate: row.item_retail_relaunch_date || null,
  itemRetailLaunchDate: row.item_retail_launch_date || null,
  itemWhslLaunchDate: row.item_whsl_launch_date || null,
  projInDCDate: row.est_in_dc_date,
  poQty: row.quantity || 0,
  receivedQty: row.quantity_received || 0,
  billedQty: row.quantity_billed || 0,
  geodisScannedArrival: row.geodis_scanned_arrival || null,
  openQty: row.open_qty || 0,
  putawayQty: parseFloat(row.putaway_qty) || 0,
  unitCost: parseFloat(row.item_rate) || 0,
  launchStatus: row.launch_status || '',
  channel: row.virtual_inventory_channel?.replace('FG - ', '') || '',
  cc: row.cc || '',
  coo: row.po_coo || '',
  npdIndex: row.npd_index || '',
  // X-Factory date: prefer actual, then confirmed, then requested
  xFtyDate: row.actual_x_factory_date || row.conf_x_factory_date || row.req_x_factory_date || null,
  // Computed fields
  season: computeLaunchSeason(row, effectiveLaunchDate, isCarryover),  // Uses effective launch date for season
  missingEstInDC,  // Flag for Carryover POs missing Est In DC date
  poSeason: row.season || '',  // Original PO season from NetSuite (kept for reference)
  month: getMonthName(effectiveLaunchDate),
  size: row.size || '',
  material: row.material || '',
  gender: row.gender || '',
  lifecycle: row.lifecycle || '',
  ecommLaunchStatus: row.ecomm_launch_status || '',
};
};

// Default page size
const PAGE_SIZE = 10;

// Module-level cache for filter options (rarely change, avoids re-querying on every mount)
let filterOptionsCache = { data: null, timestamp: 0 };

export function DataProvider({ children }) {
  // PO Data state
  const [poData, setPOData] = useState([]);
  const [poDataAll, setPODataAll] = useState([]); // ALL filtered data (no pagination) for metrics/charts
  const [poDataUnfiltered, setPODataUnfiltered] = useState([]); // ALL data without global filters (for Launch Line Up)
  const unfilteredFetchedRef = useRef(false); // Track if unfiltered data has been fetched
  const [totalCount, setTotalCount] = useState(0);
  const [rawLineCount, setRawLineCount] = useState(0); // Raw count from purchase_orders table
  const [lastImportDate, setLastImportDate] = useState(null); // Last data import timestamp
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  
  // Filter state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({ 
    season: [],       // Multi-select
    category: [],     // Multi-select
    channel: [],      // Multi-select
    vendor: [],       // Multi-select (Factory)
    coo: [],          // Multi-select (Country of Origin)
    launchStatus: [], // Multi-select (New Product vs Carryover)
    material: []      // Multi-select (Material type)
  });
  const [period, setPeriod] = useState('all'); // Single select for period
  const [sortConfig, setSortConfig] = useState({ 
    key: 'launch_date', 
    dir: 'desc' 
  });
  
  // Filter options (loaded once from DB)
  const [filterOptions, setFilterOptions] = useState({
    seasons: [],
    categories: [],
    channels: [],
    vendors: [],
    coos: [],
    launchStatuses: [],
    materials: [],
    years: [],
    quarters: []
  });
  
  // Dashboard stats (from Supabase view)
  const [stats, setStats] = useState({
    total: 0,
    totalUnits: 0,
    receivedUnits: 0,
    totalValue: 0,
    receivedValue: 0,
    fillRate: 0,
    late: 0,
    onTime: 0
  });
  
  // Notes and reports (existing functionality)
  const [notes, setNotes] = useState({ Footwear: [], Accessories: [], Apparel: [] });
  const [reports, setReports] = useState({});

  // Inventory data by CC (for OH Inventory column)
  const [inventoryByCC, setInventoryByCC] = useState({});
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const inventoryFetchedRef = useRef(false); // Track if inventory has been fetched
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  
  // Legacy support
  const [saddleUpData, setSaddleUpData] = useState([]);

  // ============================================================
  // FETCH PO DATA WITH SERVER-SIDE FILTERING & PAGINATION
  // ============================================================
  
  // Build RPC params from current filters (shared by paginated and full fetches)
  const buildRPCParams = useCallback(() => ({
    p_categories: filters.category?.length > 0 ? filters.category : null,
    p_vendors: filters.vendor?.length > 0 ? filters.vendor : null,
    p_seasons: filters.season?.length > 0 ? filters.season : null,
    p_coos: filters.coo?.length > 0 ? filters.coo : null,
    p_launch_statuses: filters.launchStatus?.length > 0 ? filters.launchStatus : null,
    p_materials: filters.material?.length > 0 ? filters.material : null,
    p_channels: filters.channel?.length > 0 ? filters.channel : null,
    p_search: debouncedSearch || null
  }), [filters, debouncedSearch]);

  // Map app sort keys to DB column names
  const sortColumnMap = {
    'timing': 'ecomm_launch_status',
    'variance': 'est_in_dc_date',
    'productName': 'display_name',
    'colorName': 'color',
    'vendor': 'vendor_name',
    'poNumber': 'document_number',
    'launchDate': 'launch_date',
    'poQty': 'quantity',
    'poQtyReceived': 'quantity_received'
  };

  const fetchPOData = useCallback(async () => {
    setDataLoading(true);
    try {
      const sortColumn = sortColumnMap[sortConfig.key] || sortConfig.key;
      const from = (page - 1) * pageSize;

      // Use server-side RPC for filtering — select('*') so columns are defined by the RPC, not here
      const { data, count, error } = await supabase
        .rpc('get_po_data', buildRPCParams(), { count: 'exact' })
        .select('*')
        .order(sortColumn, { ascending: sortConfig.dir === 'asc' })
        .range(from, from + pageSize - 1);

      if (error) throw error;

      const mappedData = (data || []).map(mapPOFromSupabase);
      setPOData(mappedData);
      setTotalCount(count || 0);

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching PO data:', error);
      setPOData([]);
      setTotalCount(0);
    } finally {
      setDataLoading(false);
    }
  }, [filters, debouncedSearch, sortConfig, page, pageSize, buildRPCParams]);

  // ============================================================
  // FETCH ALL FILTERED DATA (no pagination - for metrics/charts)
  // Uses PARALLEL batch fetching for speed
  // ============================================================

  const fetchAllFilteredData = useCallback(async () => {
    try {
      const batchSize = 1000;
      const rpcParams = buildRPCParams();
      const sortColumn = sortColumnMap[sortConfig.key] || sortConfig.key;

      // Step 1: Fetch first batch AND get total count in one call
      const { data: firstBatch, count, error: firstError } = await supabase
        .rpc('get_po_data', rpcParams, { count: 'exact' })
        .select('*')
        .order(sortColumn, { ascending: sortConfig.dir === 'asc' })
        .range(0, batchSize - 1);

      if (firstError) throw firstError;
      if (!count || count === 0) {
        setPODataAll([]);
        return;
      }

      // Step 2: If more batches needed, fetch remaining in parallel
      let allData = firstBatch || [];

      if (count > batchSize) {
        const numBatches = Math.ceil(count / batchSize);
        const batchPromises = [];

        for (let i = 1; i < numBatches; i++) {
          const from = i * batchSize;
          const to = from + batchSize - 1;
          batchPromises.push(
            supabase.rpc('get_po_data', rpcParams)
              .select('*')
              .order(sortColumn, { ascending: sortConfig.dir === 'asc' })
              .range(from, to)
          );
        }

        const results = await Promise.all(batchPromises);
        allData = allData.concat(results.flatMap(r => r.data || []));
      }

      const mappedData = allData.map(mapPOFromSupabase);
      setPODataAll(mappedData);

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching all filtered PO data:', error);
      setPODataAll([]);
    }
  }, [filters, debouncedSearch, sortConfig, buildRPCParams]);

  // ============================================================
  // FETCH ALL UNFILTERED DATA (for Launch Line Up - independent of global filters)
  // ============================================================

  const fetchAllUnfilteredData = useCallback(async () => {
    // Only fetch once
    if (unfilteredFetchedRef.current) return;

    // Fast path: one server execution, one response (paging a set-returning
    // RPC re-executes the ENTIRE function per 1,000-row page — ~47 full runs
    // per cold load). Falls back to the paged path if the blob RPC is absent
    // (deploy skew) or fails.
    try {
      const { data: blob, error: blobError } = await supabase.rpc('get_po_data_blob');
      if (!blobError && Array.isArray(blob?.rows)) {
        setPODataUnfiltered(blob.rows.map(mapPOFromSupabase));
        unfilteredFetchedRef.current = true;
        return;
      }
      if (blobError) console.warn('get_po_data_blob unavailable, falling back to paged fetch:', blobError.message);
    } catch (e) {
      console.warn('get_po_data_blob failed, falling back to paged fetch:', e);
    }

    try {
      const batchSize = 1000;

      // Fetch first batch AND get total count in one call
      const { data: firstBatch, count, error: firstError } = await supabase
        .rpc('get_po_data', {}, { count: 'exact' })
        .select('*')
        .range(0, batchSize - 1);

      if (firstError) throw firstError;
      if (!count || count === 0) {
        setPODataUnfiltered([]);
        return;
      }

      let allData = firstBatch || [];

      if (count > batchSize) {
        const numBatches = Math.ceil(count / batchSize);
        const batchPromises = [];

        for (let i = 1; i < numBatches; i++) {
          const from = i * batchSize;
          const to = from + batchSize - 1;
          batchPromises.push(
            supabase.rpc('get_po_data', {}).select('*').range(from, to)
          );
        }

        const results = await Promise.all(batchPromises);
        allData = allData.concat(results.flatMap(r => r.data || []));
      }

      const mappedData = allData.map(mapPOFromSupabase);
      setPODataUnfiltered(mappedData);
      unfilteredFetchedRef.current = true;

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching unfiltered PO data:', error);
      setPODataUnfiltered([]);
    }
  }, []);

  // ============================================================
  // FETCH RAW LINE COUNT (for Data Corral display)
  // ============================================================

  const fetchRawLineCount = useCallback(async () => {
    try {
      const { count, error } = await supabase
        .from('purchase_orders')
        .select('*', { count: 'exact', head: true });
      
      if (error) throw error;
      setRawLineCount(count || 0);
    } catch (error) {
      // eslint-disable-next-line no-console
          console.error('Error fetching raw line count:', error);
      setRawLineCount(0);
    }
  }, []);

  // ============================================================
  // FETCH LAST IMPORT DATE
  // ============================================================
  
  const fetchLastImportDate = useCallback(async () => {
    try {
      // First try import_batches table
      const { data: batchData } = await supabase
        .from('import_batches')
        .select('created_at')
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (batchData?.created_at) {
        setLastImportDate(batchData.created_at);
        return;
      }
      
      // Fallback: get most recent record from purchase_orders
      const { data: poData } = await supabase
        .from('purchase_orders')
        .select('created_at')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (poData?.created_at) {
        setLastImportDate(poData.created_at);
        return;
      }
      
      // Final fallback: use current timestamp if data exists
      const { count } = await supabase
        .from('purchase_orders')
        .select('*', { count: 'exact', head: true });
      
      if (count && count > 0) {
        setLastImportDate(new Date().toISOString());
      } else {
        setLastImportDate(null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
          console.error('Error fetching last import date:', error);
      // If there's an error but we have data, show current time
      setLastImportDate(new Date().toISOString());
    }
  }, []);

  // ============================================================
  // FETCH FILTER OPTIONS (distinct values from DB)
  // ============================================================
  
  const fetchFilterOptions = useCallback(async () => {
    try {
      // Check if cached data is still fresh (within 5 minutes)
      if (filterOptionsCache.data && Date.now() - filterOptionsCache.timestamp < 300000) {
        setFilterOptions(filterOptionsCache.data);
        return;
      }

      // Use database function to get all distinct filter values efficiently
      const { data, error } = await supabase.rpc('get_filter_options');

      if (error) throw error;

      // Compute years and quarters from launch dates
      const yearsSet = new Set();
      const quartersSet = new Set();
      (data?.launchDates || []).forEach(dateStr => {
        if (dateStr) {
          // Handle date-only strings (YYYY-MM-DD) to avoid timezone shift
          let d;
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            const [year, month, day] = dateStr.split('-').map(Number);
            d = new Date(year, month - 1, day);
          } else {
            d = new Date(dateStr);
          }
          const year = d.getFullYear().toString();
          const quarter = `Q${Math.floor(d.getMonth() / 3) + 1}`;
          yearsSet.add(year);
          quartersSet.add(`${quarter}-${year}`);
        }
      });

      const result = {
        seasons: data?.seasons || [],
        categories: data?.categories || [],
        channels: data?.channels || [],
        vendors: data?.vendors || [],
        coos: data?.coos || [],
        launchStatuses: data?.launchStatuses || [],
        materials: data?.materials || [],
        years: [...yearsSet].sort(),
        quarters: [...quartersSet].sort()
      };

      // Update module-level cache
      filterOptionsCache = { data: result, timestamp: Date.now() };

      setFilterOptions(result);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching filter options:', error);
    }
  }, []);

  // ============================================================
  // FETCH INVENTORY BY CC (for OH Inventory column)
  // Fetches ALL FG inventory once at initialization - not dependent on filters
  // ============================================================

  const fetchInventoryByCC = useCallback(async () => {
    // Only run once - use ref to prevent re-fetching
    if (inventoryFetchedRef.current) {
      return;
    }
    inventoryFetchedRef.current = true;
    setInventoryLoading(true);

    try {
      // Cortex: same shape as the old RPC, scoped to FG pools (Ecomm / Retail / Wholesale / Corp / Marketing)
      const inventoryMap = await cortexInventoryByCC();
      setInventoryByCC(inventoryMap);
      setInventoryLoading(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching inventory by CC:', error);
      inventoryFetchedRef.current = false; // Allow retry on error
      setInventoryByCC({});
      setInventoryLoading(false);
    }
  }, []);

  // ============================================================
  // FETCH CC-AGGREGATED DATA (for Launch Line Up, Round Up Report)
  // Uses server-side RPC to avoid fetching 32k rows
  // ============================================================
  const [ccAggregatedData, setCCAggregatedData] = useState([]);
  const [ccDataLoading, setCCDataLoading] = useState(false);

  const fetchCCAggregatedData = useCallback(async (options = {}) => {
    const {
      daysAhead = 365,
      categories = null,
      vendors = null,
      channels = null,
      launchStatuses = null,
      excludeCarryover = false
    } = options;

    setCCDataLoading(true);
    try {
      const { data, error } = await rpcAll(supabase, 'get_launch_lineup_data', {
        p_days_ahead: daysAhead,
        p_categories: categories,
        p_vendors: vendors,
        p_channels: channels,
        p_launch_statuses: launchStatuses,
        p_exclude_carryover: excludeCarryover
      });

      if (error) throw error;

      setCCAggregatedData(data || []);
      setCCDataLoading(false);
      return data || [];
    } catch (error) {
      console.error('Error fetching CC aggregated data:', error);
      setCCAggregatedData([]);
      setCCDataLoading(false);
      return [];
    }
  }, []);

  // ============================================================
  // FETCH NOTES AND REPORTS (existing functionality)
  // ============================================================

  const fetchNotesAndReports = useCallback(async () => {
    try {
      // Fetch notes
      const { data: notesData, error: notesError } = await supabase
        .from('trail_notes')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (notesError) throw notesError;
      
      const groupedNotes = { Footwear: [], Accessories: [], Apparel: [] };
      notesData?.forEach(note => {
        if (groupedNotes[note.category]) {
          groupedNotes[note.category].push({
            id: note.id,
            text: note.text,
            author: note.author || null,
            ts: new Date(note.created_at).toLocaleString()
          });
        }
      });
      setNotes(groupedNotes);
      
      // Fetch reports
      const { data: reportsData, error: reportsError } = await supabase
        .from('police_reports')
        .select('*');
      
      if (reportsError) throw reportsError;
      
      const reportsObj = {};
      reportsData?.forEach(report => {
        reportsObj[report.po_key] = {
          text: report.text,
          owner: report.owner,
          ts: new Date(report.updated_at).toLocaleString()
        };
      });
      setReports(reportsObj);
      
    } catch (error) {
      // eslint-disable-next-line no-console
          console.error('Error loading notes/reports:', error);
    }
  }, []);

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  // Pull the org-wide calc settings into the local cache once per app load
  useEffect(() => { syncGlobalConfig(); }, []);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      await Promise.all([
        fetchFilterOptions(),
        fetchNotesAndReports(),
        fetchRawLineCount(),
        fetchLastImportDate()
        // NOTE: fetchInventoryByCC removed - now lazy loaded when Trail Map/Frontier sections open
      ]);
      setLoading(false);
    };
    initialize();
  }, [fetchFilterOptions, fetchNotesAndReports, fetchRawLineCount, fetchLastImportDate]);

  // Fetch paginated PO data immediately when filters/search/sort/page change
  useEffect(() => {
    fetchPOData();
  }, [fetchPOData]);

  // Defer fetching ALL filtered data - runs after initial render to not block page load
  // This populates data for Frontier, Trail Map, stats, and other sections
  // Fetch stats using server-side RPC (replaces batch fetching ALL data)
  const fetchStats = useCallback(async () => {
    try {
      // Build filter arrays for RPC
      const p_categories = filters.category?.length > 0 ? filters.category : null;
      const p_vendors = filters.vendor?.length > 0 ? filters.vendor : null;
      const p_seasons = filters.season?.length > 0 ? filters.season : null;
      const p_coos = filters.coo?.length > 0 ? filters.coo : null;
      const p_launch_statuses = filters.launchStatus?.length > 0 ? filters.launchStatus : null;
      const p_materials = filters.material?.length > 0 ? filters.material : null;
      const p_channels = filters.channel?.length > 0 ? filters.channel : null;
      const p_search = debouncedSearch || null;

      const { data, error } = await supabase.rpc('get_po_dashboard_stats', {
        p_categories,
        p_vendors,
        p_seasons,
        p_coos,
        p_launch_statuses,
        p_materials,
        p_channels,
        p_search
      });

      if (error) throw error;

      if (data) {
        const totalUnits = data.totalUnits || 0;
        const receivedUnits = data.receivedUnits || 0;
        const openUnits = data.openUnits || 0;
        const openValue = data.openValue || 0;
        const fillRate = totalUnits > 0 ? Math.round(receivedUnits / totalUnits * 100) : 0;

        setStats({
          total: data.uniquePOs || 0,
          totalUnits,
          receivedUnits,
          totalValue: 0, // Not computed by RPC yet
          receivedValue: 0, // Not computed by RPC yet
          fillRate,
          late: 0, // Would need additional RPC logic
          onTime: 0,
          rOT: 0,
          rL: 0,
          pL: 0,
          atRiskUnits: openUnits,
          atRiskValue: openValue,
          otdRate: 0
        });
      }
    } catch (error) {
      console.error('Error fetching stats via RPC:', error);
    }
  }, [filters, debouncedSearch]);

  // Fetch stats when filters change (using RPC, not batch fetching)
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // NOTE: Removed automatic fetchAllFilteredData call - it was causing timeouts by fetching 32k rows
  // Pages that need aggregated data should use the new RPCs instead:
  // - get_launch_lineup_data() for Launch Line Up, Round Up Report
  // - get_supply_plan_data() for Supply Plan
  // - get_po_dashboard_stats() for stats (already using this)

  // Debounce search to avoid query per keystroke (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filters, debouncedSearch]);

  // NOTE: Inventory is now fetched once at initialization (not dependent on poDataAll)
  // This prevents race conditions from React Strict Mode double-renders

  // ============================================================
  // FILTER ACTIONS
  // ============================================================
  
  // Set a filter value (accepts array for multi-select or single value)
  const setFilter = (key, value) => {
    setFilters(f => ({
      ...f,
      [key]: Array.isArray(value) ? value : [value]
    }));
  };
  
  const clearFilters = () => {
    setFilters({ season: [], category: [], channel: [], vendor: [], coo: [], launchStatus: [] });
    setSearch('');
    setPeriod('all');
  };
  
  // Count total active filters (sum of all array lengths + search)
  const activeFilterCount = Object.values(filters).reduce((sum, arr) => sum + (arr?.length || 0), 0) 
    + (search ? 1 : 0) 
    + (period !== 'all' ? 1 : 0);

  // ============================================================
  // SORTING
  // ============================================================
  
  const setSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // ============================================================
  // PAGINATION
  // ============================================================
  
  const totalPages = Math.ceil(totalCount / pageSize);
  
  const goToPage = (p) => {
    setPage(Math.max(1, Math.min(p, totalPages)));
  };
  
  const nextPage = () => goToPage(page + 1);
  const prevPage = () => goToPage(page - 1);

  // ============================================================
  // IMPORT DATA TO SUPABASE
  // ============================================================
  
  const importData = async (file, onProgress) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const text = evt.target.result;
          // Remove BOM if present
          const cleanText = text.replace(/^\uFEFF/, '');
          
          if (!file.name.endsWith('.csv')) {
            reject(new Error('Only CSV files are supported'));
            return;
          }
          
          // Simple CSV parser (replaces papaparse to avoid build issues)
          const parseCSV = (text) => {
            const rows = [];
            const lines = text.split(/\r?\n/);
            for (const line of lines) {
              if (!line.trim()) continue; // skip empty lines
              const row = [];
              let inQuotes = false;
              let field = '';
              for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') {
                  if (inQuotes && line[i + 1] === '"') {
                    field += '"';
                    i++;
                  } else {
                    inQuotes = !inQuotes;
                  }
                } else if (char === ',' && !inQuotes) {
                  row.push(field);
                  field = '';
                } else {
                  field += char;
                }
              }
              row.push(field);
              rows.push(row);
            }
            return rows;
          };

          const allRows = parseCSV(cleanText);
          const headers = allRows[0].map(h => h.trim());
          const dataRows = allRows.slice(1);
          
          // Find the second "Name" column index (that's the actual vendor name)
          let nameCount = 0;
          let vendorNameColIndex = -1;
          headers.forEach((h, idx) => {
            if (h === 'Name') {
              nameCount++;
              if (nameCount === 2) {
                vendorNameColIndex = idx;
              }
            }
          });

          // Map CSV headers to Supabase columns
          const headerMap = {
            'Internal ID': 'internal_id',
            'Creation Date': 'creation_date',
            'PO Internal ID': 'po_internal_id',
            'Memo (Main)': 'memo',
            'Document Number': 'document_number',
            'Shipment Number': 'shipment_number',
            'Shipment Method': 'shipment_method',
            'Requested Ex-Factory Date': 'requested_ex_factory_date',
            'Confirmed Ex-Factory Date': 'confirmed_ex_factory_date',
            'Actual Ex-Factory Date': 'actual_ex_factory_date',
            'Est. Arrival Date': 'est_arrival_date',
            'Est. In-DC Date': 'est_in_dc_date',
            'Ecomm Launch Status': 'ecomm_launch_status',
            'Launch Date': 'launch_date',
            'Item Internal ID': 'item_internal_id',
            'Name': 'name',
            'Vendor Name': 'vendor_name',
            'CC': 'cc',
            'Display Name': 'display_name',
            'Quantity': 'quantity',
            'Quantity Fulfilled/Received': 'quantity_received',
            'Open Qty': 'open_qty',
            'Item Rate': 'item_rate',
            'Open Amount': 'open_amount',
            'Ship Window Start': 'ship_window_start',
            'Ship Window End': 'ship_window_end',
            'Virtual Inventory Channel': 'virtual_inventory_channel',
            'Location': 'location',
            'PO COO': 'po_coo',
            'Terms': 'terms',
            'UPC Code': 'upc_code',
            'Division': 'division',
            'Merchandise Hierarchy': 'merchandise_hierarchy',
            'Merchandise Department': 'merchandise_department',
            'Merchandise Class': 'merchandise_class',
            'Merchandise Sub-Class': 'merchandise_sub_class',
            'Gender': 'gender',
            'Style': 'style',
            'Season': 'season',
            'Material': 'material',
            'Lifecycle': 'lifecycle',
            'Color': 'color',
            'Size': 'size',
            'Sellable/Non-Sellable': 'sellable',
            'Exotic/Non Exotic': 'exotic',
            'Product Style': 'product_style',
            'Item Status': 'item_status',
            'Heel Material': 'heel_material',
            'Line ID': 'line_id',
            'Launch Status': 'launch_status',
            'PDP Group': 'pdp_group',
            'NPD Index #': 'npd_index'
          };
          
          // Parse date in M/D/YY format
          const parseDate = (dateStr) => {
            if (!dateStr) return null;
            const parts = dateStr.split('/');
            if (parts.length !== 3) return null;
            const month = parseInt(parts[0]);
            const day = parseInt(parts[1]);
            let year = parseInt(parts[2]);
            if (year < 100) year += 2000;
            return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
          };
          
          // Parse amount (remove commas and spaces)
          const parseAmount = (amtStr) => {
            if (!amtStr) return 0;
            return parseFloat(String(amtStr).replace(/[,\s]/g, '')) || 0;
          };
          
          // Build column index map
          const colIndexMap = {};
          headers.forEach((h, idx) => {
            const dbCol = headerMap[h];
            if (dbCol && !colIndexMap[dbCol]) {  // Only use first occurrence
              colIndexMap[dbCol] = idx;
            }
          });
          
          // Build records from parsed data
          const records = [];
          for (const vals of dataRows) {
            if (!vals || vals.length === 0) continue;
            
            const row = {};
            
            // Map each column
            Object.entries(colIndexMap).forEach(([dbCol, idx]) => {
              let val = vals[idx] || '';
              
              // Date fields
              if (['creation_date', 'requested_ex_factory_date', 'confirmed_ex_factory_date', 
                   'actual_ex_factory_date', 'est_arrival_date', 'est_in_dc_date', 'launch_date',
                   'ship_window_start', 'ship_window_end'].includes(dbCol)) {
                val = parseDate(val);
              }
              // Integer fields
              else if (['quantity', 'quantity_received', 'open_qty', 'line_id'].includes(dbCol)) {
                val = parseInt(val) || 0;
              }
              // Decimal fields
              else if (['item_rate', 'open_amount'].includes(dbCol)) {
                val = parseAmount(val);
              }
              
              row[dbCol] = val;
            });
            
            // Override vendor_name with the second "Name" column (actual vendor)
            if (vendorNameColIndex >= 0 && vals[vendorNameColIndex]) {
              row.vendor_name = vals[vendorNameColIndex].trim();
            }
            
            // Skip rows without document_number
            if (row.document_number) {
              records.push(row);
            }
          }
          
          // Create import batch record
          const { data: { user } } = await supabase.auth.getUser();
          const { data: batch, error: batchError } = await supabase
            .from('import_batches')
            .insert({
              filename: file.name,
              record_count: records.length,
              imported_by: user?.email || 'unknown',
              status: 'in_progress'
            })
            .select()
            .single();
          
          if (batchError) console.warn('Could not create import batch:', batchError);
          
          // Delete existing data before import (full refresh)
          const { error: deleteError } = await supabase
            .from('purchase_orders')
            .delete()
            .neq('id', '00000000-0000-0000-0000-000000000000');
          
          if (deleteError) {
            console.warn('Delete error (continuing with upsert):', deleteError);
          }
          
          // Deduplicate records by document_number + line_id (keep last occurrence)
          const recordMap = new Map();
          records.forEach(r => {
            const key = `${r.document_number}|${r.line_id}`;
            recordMap.set(key, r);
          });
          const uniqueRecords = Array.from(recordMap.values());

          // Insert in batches of 500 using upsert to handle any duplicates
          const batchSize = 500;
          let imported = 0;
          
          for (let i = 0; i < uniqueRecords.length; i += batchSize) {
            const chunk = uniqueRecords.slice(i, i + batchSize).map(r => ({
              ...r,
              import_batch_id: batch?.id
            }));
            
            const { error } = await supabase
              .from('purchase_orders')
              .upsert(chunk, { 
                onConflict: 'document_number,line_id',
                ignoreDuplicates: false 
              });
            
            if (error) {
              // eslint-disable-next-line no-console
          console.error('Batch upsert error:', error);
              throw error;
            }
            
            imported += chunk.length;
            if (onProgress) {
              onProgress(Math.round(imported / uniqueRecords.length * 100));
            }
          }
          
          // Update batch status
          if (batch?.id) {
            await supabase
              .from('import_batches')
              .update({ status: 'completed' })
              .eq('id', batch.id);
          }
          
          // Refresh data (stats now fetched via RPC)
          await fetchFilterOptions();
          await fetchPOData();
          await fetchStats();
          await fetchRawLineCount();
          
          resolve(uniqueRecords.length);
          
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Import error:', err);
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  // ============================================================
  // NOTES CRUD
  // ============================================================
  
  const addNote = async (category, text) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('trail_notes')
        .insert({ category, text, author: user?.email })
        .select()
        .single();
      
      if (error) throw error;
      
      setNotes(prev => ({
        ...prev,
        [category]: [{
          id: data.id,
          text: data.text,
          author: data.author,
          ts: new Date(data.created_at).toLocaleString()
        }, ...(prev[category] || [])]
      }));
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
          console.error('Error adding note:', error);
      return false;
    }
  };

  const deleteNote = async (category, noteId) => {
    try {
      const { error } = await supabase
        .from('trail_notes')
        .delete()
        .eq('id', noteId);
      
      if (error) throw error;
      
      setNotes(prev => ({
        ...prev,
        [category]: (prev[category] || []).filter(n => n.id !== noteId)
      }));
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
          console.error('Error deleting note:', error);
      return false;
    }
  };

  // ============================================================
  // REPORTS CRUD
  // ============================================================
  
  const saveReport = async (poKey, text, owner) => {
    if (text.trim()) {
      try {
        const { error } = await supabase
          .from('police_reports')
          .upsert(
            { po_key: poKey, text: text.trim(), owner, updated_at: new Date().toISOString() },
            { onConflict: 'po_key' }
          );
        if (error) throw error;
        setReports(p => ({
          ...p,
          [poKey]: { text: text.trim(), owner, ts: new Date().toLocaleString() }
        }));
        return true;
      } catch (error) {
        // eslint-disable-next-line no-console
          console.error('Error saving report:', error);
        return false;
      }
    }
    return deleteReport(poKey);
  };

  const deleteReport = async (poKey) => {
    try {
      const { error } = await supabase
        .from('police_reports')
        .delete()
        .eq('po_key', poKey);
      if (error) throw error;
      setReports(p => {
        const newReports = { ...p };
        delete newReports[poKey];
        return newReports;
      });
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
          console.error('Error deleting report:', error);
      return false;
    }
  };

  // ============================================================
  // HIGHLIGHTS (computed from current page data)
  // ============================================================
  
  const highlights = useMemo(() => ({
    late: poData.filter(r => {
      const isLate = parseLocalDate(r.projInDCDate) > parseLocalDate(r.effectiveLaunchDate || r.launchDate);
      return isLate && r.poQtyReceived < r.poQty;
    }),
    partial: poData.filter(r => r.poQtyReceived > 0 && r.poQtyReceived < r.poQty),
    npd: poData.filter(r => r.launchStatus === 'Launch PO'),
    pv: []
  }), [poData]);

  // ============================================================
  // CONTEXT VALUE
  // ============================================================
  // Memoize context value to prevent cascading re-renders
  const value = useMemo(() => ({
    // PO Data
    filtered: poData,           // Paginated data for tables (25 per page)
    filteredAll: poDataAll,     // ALL filtered data (no pagination) for metrics/charts
    rawData: poData,
    setRawData: () => {}, // No-op, data comes from Supabase

    // Pagination
    page,
    setPage: goToPage,
    pageSize,
    totalCount,
    rawLineCount, // Raw line count from purchase_orders table (for Data Corral)
    lastImportDate, // Last successful import timestamp
    totalPages,
    nextPage,
    prevPage,

    // Stats & Highlights
    stats,
    highlights,
    filterOptions,

    // Filters
    search,
    setSearch,
    filters,
    setFilter,
    clearFilters,
    activeFilterCount,

    // Sorting
    sortConfig,
    setSort,

    // Period (legacy)
    period,
    setPeriod,

    // Notes & Reports
    notes,
    reports,
    addNote,
    deleteNote,
    saveReport,
    deleteReport,

    // Import
    importData,

    // Loading states
    loading,
    dataLoading,

    // Refresh function
    refresh: fetchPOData,

    // Legacy
    saddleUpData,
    setSaddleUpData,

    // Inventory by CC (for OH Inventory column) - lazy loaded
    inventoryByCC,
    inventoryLoading,
    fetchInventoryByCC,

    // Unfiltered data for Launch Line Up (independent of global filters)
    allUnfiltered: poDataUnfiltered,
    fetchAllUnfiltered: fetchAllUnfilteredData,

    // CC-aggregated data (from RPC - much faster than batch fetching)
    ccAggregatedData,
    ccDataLoading,
    fetchCCAggregatedData,

    // Manual fetch for pages that need full filtered data (expensive - use sparingly)
    fetchAllFilteredData
  }), [
    poData, poDataAll, page, goToPage, pageSize, totalCount, rawLineCount, lastImportDate, totalPages, nextPage, prevPage,
    stats, highlights, filterOptions, search, setSearch, filters, setFilter, clearFilters, activeFilterCount,
    sortConfig, setSort, period, setPeriod, notes, reports, addNote, deleteNote, saveReport, deleteReport,
    importData, loading, dataLoading, fetchPOData, saddleUpData, setSaddleUpData, inventoryByCC, inventoryLoading, fetchInventoryByCC,
    poDataUnfiltered, fetchAllUnfilteredData, ccAggregatedData, ccDataLoading, fetchCCAggregatedData, fetchAllFilteredData
  ]);

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}
